// src/views/Settings/OrgSettings.jsx
//
// El alcance de la organización: lo que posee el contenedor, no la cuenta que
// hay dentro. Miembros y roles cuelgan de aquí porque `memberships` cuelga de
// `organizations`; la facturación consolidada también, porque el pool vive en
// `organization_subscriptions`.
//
// Antes esto era `OrganizationDetailView`, un modal a pantalla completa sin URL:
// no se podía enlazar una sección, recargar perdía el sitio, y el botón de atrás
// del navegador cerraba la app en vez de la pantalla.
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrgOverviewSection from './org/OrgOverviewSection';
import OrgMembersSection from './org/OrgMembersSection';
import OrgBillingSection from './org/OrgBillingSection';
import OrgAccountsSection from './org/OrgAccountsSection';
import OrgSettingsSection from './org/OrgSettingsSection';
import SectionLoader from './SectionLoader';
import { useOrganizations } from '../../hooks/useOrganizations';
import useOrgRole from '../../hooks/useOrgRole';
import {
  SCOPES,
  isSettingsSection,
  sectionsForScope,
  settingsPath,
} from '../../lib/settingsNav';

export default function OrgSettings() {
  const { orgId, section } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    organizations = [],
    organizationsLoaded,
    loadOrganizations,
    changeContext,
    currentContext,
  } = useOrganizations();

  const organization = organizations.find((o) => o.id === orgId) || null;
  // `GET /organizations` no trae la membresía: el rol se resuelve contra la
  // lista de miembros. El modal viejo lo daba por 'owner' y enseñaba la
  // administración a cualquiera.
  const { role, canManage, settled: roleSettled } = useOrgRole(orgId);

  const allowed = sectionsForScope(SCOPES.ORG, { canManage }).map((s) => s.id);
  const active =
    isSettingsSection(SCOPES.ORG, section) && allowed.includes(section) ? section : null;

  // Una sección que el rol no permite no se muestra "vacía": se sustituye por la
  // primera que sí, porque una opción que no funciona es peor que no tenerla.
  useEffect(() => {
    if (!organization || !roleSettled || active) return;
    navigate(settingsPath(SCOPES.ORG, allowed[0] || 'overview', { orgId }), { replace: true });
  }, [organization, roleSettled, active, allowed, orgId, navigate]);

  // La lista llega del bootstrap de auth. Mientras no haya terminado de llegar
  // no se puede afirmar que esta organización no exista — y una lista vacía no
  // es prueba de nada, es la lista inicial.
  if (!organization) {
    // El cargando vive dentro del shell, no encima: reemplazar la pantalla
    // entera hacía desaparecer el menú y volver, que es el parpadeo que se
    // veía al entrar.
    if (!organizationsLoaded) return <SectionLoader />;
    return (
      <div className="text-gray-400">
        {t('settingsNav.orgNotFound', 'This organization is not available for your account.')}
      </div>
    );
  }

  // Sin rol resuelto no se puede decidir qué secciones existen, y pintar el
  // menú a medias para completarlo medio segundo después es peor que esperar.
  if (!roleSettled) return <SectionLoader />;
  if (!active) return null;

  return (
    <>
      {active === 'overview' && (
        <OrgOverviewSection
          organization={organization}
          role={role}
          isCurrent={currentContext?.type === 'ORGANIZATION' && currentContext?.id === organization.id}
          onSwitchContext={async (org) => {
            await changeContext({ type: 'ORGANIZATION', id: org.id, name: org.name });
            navigate('/');
          }}
        />
      )}
      {active === 'members' && (
        <OrgMembersSection organization={organization} role={role} />
      )}
      {active === 'accounts' && (
        <OrgAccountsSection organization={organization} canManage={canManage} />
      )}
      {active === 'usage' && (
        <OrgBillingSection
          organizationId={organization.id}
          organizationName={organization.name}
        />
      )}
      {active === 'settings' && (
        <OrgSettingsSection
          organization={organization}
          role={role}
          onUpdated={loadOrganizations}
          onDeleted={() => navigate('/organizations')}
        />
      )}
    </>
  );
}
